import { getLandingContent, VERTICAL_KEYS } from '@nexofacil/schemas';
import { SearchX, TriangleAlert } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError, type GetBrokerResponse } from '@/lib/api';
import { applyBrokerTheme } from '@/lib/broker-theme';
import { initBrokerPixel } from '@/lib/meta-pixel';
import { detectSubdomain } from '@/lib/subdomain';
import { useDocumentTitle } from '@/lib/use-document-title';
import { EmptyState } from '@/components/ui/empty-state';
import { LoadingState } from '@/components/ui/loading-state';
import { SeoHead } from '@/components/seo-head';
import { messages } from '@/messages';
import { PublicHomePage } from './home.js';
import { resolveLandingComponent } from './landings/registry';

// =============================================================================
// BrokerLandingPage · router slim. Resolve subdomain → broker via API,
// aplica tema (vertical default + broker override), e dispara o componente
// de landing correto através do registry (override por subdomain ou
// fallback por verticalKey).
//
// Toda a UI da landing vive em apps/web/src/pages/public/landings/:
//   - mcmv.tsx · landing MCMV (Angel, Wesley e demais corretores MCMV)
//   - pv.tsx   · landing Proteção Veicular (Risco Zero etc)
//
// Pra adicionar landing específica por cliente: cria um novo componente,
// importa em landings/registry.ts e adiciona em BROKER_OVERRIDES.
// =============================================================================

export function BrokerLandingPage() {
  const navigate = useNavigate();
  const [subdomain] = useState<string | null>(() => detectSubdomain());
  const [data, setData] = useState<GetBrokerResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useDocumentTitle(data?.broker ? `${data.broker.name} · NexoFácil` : undefined);

  useEffect(() => {
    if (!subdomain) return;
    api
      .getBroker(subdomain)
      .then((res) => {
        const content = getLandingContent(res.broker.verticalKey);
        applyBrokerTheme(content.defaultTheme, res.broker.pageContent?.theme);
        initBrokerPixel(res.broker.metaPixelId);
        setData(res);
      })
      .catch((e: unknown) => {
        if (e instanceof ApiError && e.status === 404) {
          setError(messages.errorBrokerNotFound);
        } else if (e instanceof Error) {
          setError(e.message);
        } else {
          setError(messages.errorGeneric);
        }
      });
  }, [subdomain]);

  if (!subdomain) return <PublicHomePage />;

  if (error) {
    const isBrokerNotFound = error === messages.errorBrokerNotFound;
    return (
      <PublicShell>
        <EmptyState
          icon={isBrokerNotFound ? SearchX : TriangleAlert}
          tone={isBrokerNotFound ? 'default' : 'error'}
          size="lg"
          title={
            isBrokerNotFound
              ? messages.emptyBrokerNotFoundTitle
              : messages.emptyGenericTitle
          }
          description={
            isBrokerNotFound
              ? messages.emptyBrokerNotFoundBody
              : messages.emptyGenericBody
          }
          action={{
            label: messages.ctaTryAgain,
            onClick: () => window.location.reload(),
          }}
        />
      </PublicShell>
    );
  }

  if (!data) {
    return (
      // Sem marca NexoFácil: quem chega pelo anúncio do corretor não vê outra marca primeiro.
      <div className="flex min-h-screen items-center justify-center bg-background">
        <LoadingState size="lg" />
      </div>
    );
  }

  const { broker, properties } = data;
  const content = getLandingContent(broker.verticalKey);
  const startQualification = () => navigate('/qualificar');
  const Landing = resolveLandingComponent(broker.subdomain, broker.verticalKey);

  // SEO data derivado do broker + vertical. Title combina nome + vertical
  // pra otimizar busca local; description usa broker.bio se houver, senão
  // tomba pro headline default. OG image prioriza coverPhotoUrl (visual),
  // depois avatarUrl. JSON-LD LocalBusiness habilita rich snippet do Google.
  const verticalLabel = isPvVertical(broker.verticalKey)
    ? 'Proteção Veicular'
    : broker.verticalKey === VERTICAL_KEYS.IMOBILIARIO_GERAL
      ? 'Imóveis'
      : 'MCMV';
  const seoTitle = `${broker.name} · ${verticalLabel} · NexoFácil`;
  const seoDescription =
    broker.bio ?? broker.pageContent.heroSubheadline ?? content.heroSubheadlineDefault;
  const seoImage =
    broker.pageContent.coverPhotoUrl ?? broker.avatarUrl ?? undefined;
  const seoCanonical =
    typeof window !== 'undefined'
      ? `${window.location.origin}${window.location.pathname}`
      : undefined;

  return (
    <>
      <SeoHead
        title={seoTitle}
        description={seoDescription}
        ogImage={seoImage ?? undefined}
        canonical={seoCanonical}
        jsonLd={buildLocalBusinessJsonLd(broker, seoCanonical, verticalLabel)}
      />
      <Landing
        broker={broker}
        properties={properties}
        content={content}
        onCta={startQualification}
      />
    </>
  );
}

function isPvVertical(verticalKey: string | undefined): boolean {
  return verticalKey === VERTICAL_KEYS.PROTECAO_VEICULAR;
}

/**
 * Monta um JSON-LD LocalBusiness pro Google entender quem é o broker.
 * Habilita knowledge panel e rich results. Campos vazios são omitidos pra
 * não enviar nulls (o Google reclama).
 */
function buildLocalBusinessJsonLd(
  broker: GetBrokerResponse['broker'],
  url: string | undefined,
  verticalLabel: string,
): Record<string, unknown> {
  const jsonLd: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: broker.name,
    description: broker.bio ?? `${verticalLabel} · ${broker.name}`,
  };
  if (url) jsonLd.url = url;
  if (broker.avatarUrl) jsonLd.image = broker.avatarUrl;
  if (broker.pageContent.coverPhotoUrl) {
    jsonLd.image = [broker.avatarUrl, broker.pageContent.coverPhotoUrl].filter(
      Boolean,
    );
  }
  if (broker.whatsappNumber) jsonLd.telephone = broker.whatsappNumber;
  if (broker.pageContent.address) {
    jsonLd.address = {
      '@type': 'PostalAddress',
      streetAddress: broker.pageContent.address,
      addressCountry: 'BR',
    };
  }
  return jsonLd;
}

/** Minimal NexoFácil-branded shell used for error/loading states on the public site. */
function PublicShell({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="border-b bg-background">
        <div className="container flex h-16 items-center gap-2">
          <img src="/nexofacil-icon.png" alt="" className="h-7 w-7" />
          <span className="text-sm font-semibold">NexoFácil</span>
        </div>
      </header>
      <main className="container flex flex-1 items-center justify-center">
        {children}
      </main>
      <footer className="border-t bg-background">
        <div className="container flex items-center justify-center gap-4 py-6 text-center text-xs text-muted-foreground">
          <span>NexoFácil · {new Date().getFullYear()}</span>
          <a href="/privacidade" className="underline hover:text-foreground">
            Privacidade
          </a>
        </div>
      </footer>
    </div>
  );
}
